import React from 'react';
import {
  Box, Typography, RadioGroup, FormControlLabel, Radio, Slider,
  Checkbox, Button, Snackbar, Alert, Paper, Grid, Chip, Divider,
} from '@mui/material';
import DashboardLayout from '../../../shared/layout/DashboardLayout';
import { useParams } from 'react-router-dom';
import { useCuestionarioSPForm, FRECUENCIA_SCORES, CANTIDAD_SCORES } from './hooks/useCuestionarioSPForm';

const FORM_COLORS = {
  primary: '#0d6aee',
  textMain: '#0f172a',
  textMuted: '#475569',
  border: '#dbe7f3',
};

const sectionPaperSx = {
  p: { xs: 2, md: 3 },
  mb: 3,
  borderRadius: 3,
  border: `1px solid ${FORM_COLORS.border}`,
  background: '#f8fbff',
  boxShadow: '0 4px 12px rgba(15,23,42,0.04)',
};

const radioSx = { color: FORM_COLORS.primary, '&.Mui-checked': { color: FORM_COLORS.primary } };

const OPCIONES_FRECUENCIA = [
  { label: 'Nunca',               score: 0 },
  { label: 'Una vez a la semana', score: 1 },
  { label: '2-3 veces/semana',    score: 2 },
  { label: 'Una vez al día',      score: 3 },
  { label: 'Varias veces al día', score: 4 },
  { label: 'Continuamente',       score: 5 },
];

const OPCIONES_CANTIDAD = [
  { label: 'No se me escapa nada',  score: 0 },
  { label: 'Muy poca cantidad',     score: 2 },
  { label: 'Una cantidad moderada', score: 4 },
  { label: 'Mucha cantidad',        score: 6 },
];

const OPCIONES_FACTORES = [
  'Nunca',
  'Antes de llegar al servicio',
  'Al toser o estornudar',
  'Mientras duerme',
  'Al realizar esfuerzos físicos/ejercicio',
  'Cuando termina de orinar y ya se ha vestido',
  'Sin motivo evidente',
  'De forma continua',
];

// Interpretación clínica ICIQ-SF
const getInterpretacion = (score) => {
  if (score === 0)       return { label: 'Sin incontinencia',  color: '#166534', bg: '#dcfce7' };
  if (score <= 5)        return { label: 'Leve',               color: '#854d0e', bg: '#fef9c3' };
  if (score <= 12)       return { label: 'Moderada',           color: '#92400e', bg: '#fef3c7' };
  if (score <= 18)       return { label: 'Grave',              color: '#9a3412', bg: '#ffedd5' };
  return                        { label: 'Muy grave',          color: '#991b1b', bg: '#fee2e2' };
};

const CuestionarioIncontinencia = () => {
  const { idPaciente, idCuestionario } = useParams();
  const {
    frecuencia, setFrecuencia,
    cantidad, setCantidad,
    impactoVida, setImpactoVida,
    factores, handleCheckboxChange,
    puntuacionTotal,
    openGood, setOpenGood,
    guardarDatos,
  } = useCuestionarioSPForm({ idPaciente, idCuestionario });

  const pFrecuencia = FRECUENCIA_SCORES[parseInt(frecuencia, 10)] ?? null;
  const pCantidad   = CANTIDAD_SCORES[parseInt(cantidad, 10)]   ?? null;
  const interpretacion = getInterpretacion(puntuacionTotal);

  return (
    <DashboardLayout>
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 3 }}>
        <Paper
          elevation={0}
          sx={{ width: '100%', maxWidth: 800, p: { xs: 2, md: 4 }, borderRadius: 4, border: `1px solid ${FORM_COLORS.border}` }}
        >
          <Typography variant="h4" sx={{ textAlign: 'center', color: FORM_COLORS.textMain, fontWeight: 700, mb: 1 }}>
            Cuestionario de Incontinencia Urinaria
          </Typography>
          <Typography sx={{ textAlign: 'center', color: FORM_COLORS.textMuted, mb: 1 }}>
            ICIQ-SF — Valoración del impacto de la incontinencia en la calidad de vida.
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: FORM_COLORS.textMuted, mb: 4 }}>
            Puntuación = P1 + P2 + P3 (máx. 21 pts). Cualquier puntuación &gt; 0 es diagnóstico de IU.
          </Typography>

          {/* Puntuación total en tiempo real */}
          <Paper
            elevation={0}
            sx={{
              mb: 3, p: 2.5, borderRadius: 3,
              border: `2px solid ${interpretacion.color}`,
              background: interpretacion.bg,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
            }}
          >
            <Box>
              <Typography variant="overline" sx={{ color: interpretacion.color, fontWeight: 700, letterSpacing: 1.2 }}>
                Puntuación ICIQ-SF
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h3" sx={{ fontWeight: 800, color: interpretacion.color, lineHeight: 1 }}>
                  {puntuacionTotal}
                </Typography>
                <Typography variant="body2" sx={{ color: interpretacion.color }}>/ 21</Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Chip
                label={interpretacion.label}
                sx={{ backgroundColor: interpretacion.color, color: '#fff', fontWeight: 700, fontSize: 14, px: 1 }}
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Chip size="small" label={`P1: ${pFrecuencia ?? '—'}`} variant="outlined" sx={{ borderColor: interpretacion.color, color: interpretacion.color }} />
                <Chip size="small" label={`P2: ${pCantidad ?? '—'}`}   variant="outlined" sx={{ borderColor: interpretacion.color, color: interpretacion.color }} />
                <Chip size="small" label={`P3: ${impactoVida}`}         variant="outlined" sx={{ borderColor: interpretacion.color, color: interpretacion.color }} />
              </Box>
            </Box>
          </Paper>

          {/* Pregunta 1 */}
          <Paper elevation={0} sx={sectionPaperSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700 }}>
                1. ¿Con qué frecuencia pierde orina?
              </Typography>
              {pFrecuencia !== null && (
                <Chip label={`${pFrecuencia} pt${pFrecuencia !== 1 ? 's' : ''}`} size="small"
                  sx={{ backgroundColor: '#dbeafe', color: '#1e3a8a', fontWeight: 700 }} />
              )}
            </Box>
            <Divider sx={{ mb: 1.5, borderColor: FORM_COLORS.border }} />
            <RadioGroup value={frecuencia} onChange={(e) => setFrecuencia(e.target.value)}>
              {OPCIONES_FRECUENCIA.map(({ label, score }, index) => (
                <FormControlLabel
                  key={index}
                  value={index.toString()}
                  control={<Radio sx={radioSx} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ color: FORM_COLORS.textMain }}>{label}</Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>({score} pt{score !== 1 ? 's' : ''})</Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </Paper>

          {/* Pregunta 2 */}
          <Paper elevation={0} sx={sectionPaperSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700 }}>
                2. Indique la cantidad de orina que cree que se le escapa.
              </Typography>
              {pCantidad !== null && (
                <Chip label={`${pCantidad} pts`} size="small"
                  sx={{ backgroundColor: '#dbeafe', color: '#1e3a8a', fontWeight: 700 }} />
              )}
            </Box>
            <Divider sx={{ mb: 1.5, borderColor: FORM_COLORS.border }} />
            <RadioGroup value={cantidad} onChange={(e) => setCantidad(e.target.value)}>
              {OPCIONES_CANTIDAD.map(({ label, score }, index) => (
                <FormControlLabel
                  key={index}
                  value={index.toString()}
                  control={<Radio sx={radioSx} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ color: FORM_COLORS.textMain }}>{label}</Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>({score} pts)</Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </Paper>

          {/* Pregunta 3 */}
          <Paper elevation={0} sx={sectionPaperSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700 }}>
                3. ¿En qué medida estos escapes han afectado su vida diaria?
              </Typography>
              <Chip label={`${impactoVida} pts`} size="small"
                sx={{ backgroundColor: '#dbeafe', color: '#1e3a8a', fontWeight: 700 }} />
            </Box>
            <Divider sx={{ mb: 2, borderColor: FORM_COLORS.border }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: FORM_COLORS.textMuted }}>0 — Nada</Typography>
              <Typography variant="caption" sx={{ color: FORM_COLORS.textMuted }}>10 — Mucho</Typography>
            </Box>
            <Slider
              value={impactoVida}
              onChange={(_, newValue) => setImpactoVida(newValue)}
              step={1}
              marks
              min={0}
              max={10}
              valueLabelDisplay="on"
              sx={{ color: FORM_COLORS.primary, mt: 2 }}
            />
          </Paper>

          {/* Pregunta 4 */}
          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 0.5 }}>
              4. ¿Cuándo pierde orina? (Marque todas las que correspondan)
            </Typography>
            <Typography variant="caption" sx={{ color: FORM_COLORS.textMuted, display: 'block', mb: 1.5 }}>
              Esta pregunta es diagnóstica y no puntúa en el ICIQ-SF.
            </Typography>
            <Divider sx={{ mb: 1.5, borderColor: FORM_COLORS.border }} />
            <Grid container>
              {OPCIONES_FACTORES.map((opcion, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        value={opcion}
                        checked={factores.includes(opcion)}
                        onChange={handleCheckboxChange}
                        sx={{ color: FORM_COLORS.primary, '&.Mui-checked': { color: FORM_COLORS.primary } }}
                      />
                    }
                    label={<Typography sx={{ color: FORM_COLORS.textMain }}>{opcion}</Typography>}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button variant="contained" color="primary" onClick={guardarDatos} sx={{ borderRadius: 2.5, px: 4 }}>
              Guardar respuestas
            </Button>
          </Box>
        </Paper>
      </Box>

      <Snackbar open={openGood} autoHideDuration={2000} onClose={() => setOpenGood(false)}>
        <Alert severity="success" onClose={() => setOpenGood(false)}>
          ¡Respuestas guardadas correctamente!
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default CuestionarioIncontinencia;
